import { Alert, Anchor, Badge, Button, Card, Group, Progress, Stack, Text, Title } from '@mantine/core';
import { IconBulb, IconExternalLink } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';
import ErrorMessage from '../../ErrorMessage';
import Spinner from '../../Spinner';
import useFetch from '../../../helpers/useFetch';
import { formatCurrency, getRecommendationMeta, googleAdsRecommendationsUrl } from './recommendationTypes';

const formatCount = (value) => {
  if (value == null) return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return Number.isInteger(num) ? num.toLocaleString() : num.toFixed(2);
};

const ImpactDelta = ({ recommendation, currency }) => {
  const base = recommendation?.impact?.baseMetrics;
  const potential = recommendation?.impact?.potentialMetrics;
  if (!base || !potential) return null;

  const rows = [
    { label: 'Conversions', baseVal: base.conversions, potentialVal: potential.conversions, format: formatCount },
    { label: 'Clicks', baseVal: base.clicks, potentialVal: potential.clicks, format: formatCount },
    {
      label: 'Cost',
      baseVal: base.costMicros,
      potentialVal: potential.costMicros,
      format: (v) => formatCurrency(v, currency),
    },
  ].filter((row) => row.baseVal != null || row.potentialVal != null);

  if (rows.length === 0) return null;

  return (
    <Group gap="lg" mt="xs">
      {rows.map((row) => (
        <Text size="sm" key={row.label} c="dimmed">
          {row.label}: {row.format(row.baseVal) ?? '—'} →{' '}
          <Text component="span" c="green" fw={500}>
            {row.format(row.potentialVal) ?? '—'}
          </Text>
        </Text>
      ))}
    </Group>
  );
};

const RecommendationCard = ({ recommendation, currency, onSwitchToSettings }) => {
  const meta = getRecommendationMeta(recommendation, { currency });
  const isCovered = meta.category === 'covered';

  return (
    <Card withBorder padding="md" radius="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="xs">
            <Title order={5}>{meta.title}</Title>
            <Badge color={isCovered ? 'blue' : 'gray'} variant="light" size="sm">
              {isCovered ? 'Suggested change' : 'For your information'}
            </Badge>
          </Group>
          <Text size="sm">{meta.summary}</Text>
          <ImpactDelta recommendation={recommendation} currency={currency} />
        </Stack>
        {isCovered && (
          <Button onClick={onSwitchToSettings} variant="light" size="sm">
            Go to campaign settings
          </Button>
        )}
      </Group>
    </Card>
  );
};

const OptimizationScoreHeader = ({ optimization }) => {
  if (!optimization || optimization.optimizationScore == null) return null;

  // `optimizationScore` and `optimizationScoreUplift` are fractions in [0, 1]
  // (e.g. 0.73 → 73%), matching Google Ads' Customer/Campaign fields. If the
  // backend ever switches to percentage units the math here is off by 100×.
  const scorePct = Math.round(optimization.optimizationScore * 100);
  const uplift = optimization.optimizationScoreUplift || 0;
  const hasUplift = uplift > 0;
  const potentialPct = hasUplift
    ? Math.min(100, Math.round((optimization.optimizationScore + uplift) * 100))
    : scorePct;
  const scoreColor = scorePct >= 80 ? 'green' : scorePct >= 50 ? 'yellow' : 'red';

  return (
    <Card withBorder padding="md" radius="sm">
      <Group justify="space-between" align="center" wrap="nowrap">
        <Stack gap={4} style={{ flex: 1 }}>
          <Group gap="xs" align="baseline">
            <Title order={3}>Optimization score: {scorePct}%</Title>
            {hasUplift && (
              <Text size="sm" c="dimmed">
                → potential {potentialPct}% (applying all suggestions)
              </Text>
            )}
          </Group>
          <Progress value={scorePct} size="sm" color={scoreColor} />
        </Stack>
        {optimization.optimizationScoreUrl && (
          <Anchor
            href={optimization.optimizationScoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            fw={500}
          >
            View in Google Ads
            <IconExternalLink size={14} style={{ verticalAlign: 'middle', marginLeft: 4 }} />
          </Anchor>
        )}
      </Group>
    </Card>
  );
};

const GoogleAdRecommendations = ({ googleAd, onSwitchToSettings }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();
  const googleAccount = useSelector((state) => state.google?.account);
  const domainCurrency = useSelector((state) => state.domain?.domain?.currency);
  const currency = googleAccount?.adAccountCurrency || domainCurrency;

  const [recommendations, setRecommendations] = useState();
  const [optimization, setOptimization] = useState();
  const [loadError, setLoadError] = useState();

  useEffect(() => {
    if (!googleAd?.id) return;
    let cancelled = false;
    // Reset so the spinner re-engages and stale data from a previous ad
    // doesn't briefly render while the new fetch is in flight.
    setRecommendations(undefined);
    setOptimization(undefined);
    setLoadError(undefined);
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/googleAds/${googleAd.id}/recommendations`)
      .then(({ recommendations, optimization }) => {
        if (cancelled) return;
        setRecommendations(recommendations || []);
        setOptimization(optimization || null);
      })
      .catch((e) => {
        if (!cancelled) setLoadError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId, googleAd?.id]);

  const { covered, fyi } = (() => {
    if (!recommendations) return { covered: [], fyi: [] };
    const buckets = { covered: [], fyi: [] };
    for (const rec of recommendations) {
      // Partition only reads .category, which doesn't depend on currency — skip
      // the options arg so the summary fn isn't called unnecessarily.
      const category = getRecommendationMeta(rec).category;
      if (category === 'covered') buckets.covered.push(rec);
      else buckets.fyi.push(rec);
    }
    return buckets;
  })();

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (recommendations === undefined) {
    return <Spinner />;
  }

  if (recommendations.length === 0) {
    return (
      <Stack gap="lg">
        <OptimizationScoreHeader optimization={optimization} />
        <Alert icon={<IconBulb size={18} />} color="gray" variant="light">
          No recommendations from Google right now. Recommendations usually appear once a campaign has 7–14 days of
          delivery data — check back later.
        </Alert>
      </Stack>
    );
  }

  const fyiLinkHref = optimization?.optimizationScoreUrl || googleAdsRecommendationsUrl();

  return (
    <Stack gap="lg">
      <OptimizationScoreHeader optimization={optimization} />

      {covered.length > 0 && (
        <Stack gap="sm">
          <Title order={4}>Suggested changes</Title>
          {covered.map((rec) => (
            <RecommendationCard
              key={rec.resourceName}
              recommendation={rec}
              currency={currency}
              onSwitchToSettings={onSwitchToSettings}
            />
          ))}
        </Stack>
      )}

      {fyi.length > 0 && (
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Title order={4}>For your information</Title>
            <Anchor href={fyiLinkHref} target="_blank" rel="noopener noreferrer" size="sm" fw={500}>
              Manage in Google Ads
              <IconExternalLink size={14} style={{ verticalAlign: 'middle', marginLeft: 4 }} />
            </Anchor>
          </Group>
          {fyi.map((rec) => (
            <RecommendationCard key={rec.resourceName} recommendation={rec} currency={currency} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default GoogleAdRecommendations;
